import { FC, useEffect } from "react";
import { useAppSelector, useAppTranslation } from "@/utils";
import { SDKRequestInit, bnplMerchantApis } from "@payping/api";
import { dateFormats, moment, toJalali } from "@payping/utils/dateUtils";
import { ValidEmail, ValidLocalPhoneNumber, ValidPostalCode } from "@payping/utils/validation";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Spin,
  TextArea,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import provinces from "@/utils/provinces.json";
import cities from "@/utils/cities.json";
import { CompleteMerchantProfileInfoRequest } from "@payping/api/app/api/bnplMerchantTypes";

interface FormInputs {
  firstName: string;
  lastName: string;
  birthDate: moment.Moment;
  sex: "male" | "female";
  nationalId: number;
  idCardSerialNumber: string;
  postalCode: number;
  email: string;
  cardNumber: number;
  province: string;
  city: string;
  address: string;
  phoneNumber: string;
}

interface InformationCompletingProps {
  onFinish: () => void;
  goBack: () => void;
}
const BnplActivationPageFormInformationCompleting: FC<InformationCompletingProps> = ({ onFinish, goBack }) => {
  const { t } = useAppTranslation();
  const [form] = Form.useForm<FormInputs>();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const profileInfoState = useMessagedAsync(() => bnplMerchantApis.getMerchantProfileInfo(), [], {
    success: false,
    error: true,
  });
  const contract = useAppSelector((state) => state.bnplMerchant.contract);

  const { xs } = Grid.useBreakpoint();

  const [completeProfileState, completeProfile] = useMessagedAsyncFn(
    async (body: CompleteMerchantProfileInfoRequest) => {
      try {
        const res = await bnplMerchantApis.completeMerchantProfileInfo(body, { isRaw: true } as SDKRequestInit);
        onFinish();
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              // address required
              const errors = jsonError?.errors;
              const messages = (
                errors instanceof Array
                  ? errors.map((item: any) => (typeof item?.message === "string" ? item?.message : ""))
                  : []
              ).filter((item: string) => item !== "");
              throw new Error(messages.length ? JSON.stringify(messages.join(" , ")) : t("common.serverError"));
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [],
    { loading: false },
    { error: true },
  );

  const provinceWatch = Form.useWatch("province", form);
  const provinceCitiesList = provinceWatch ? (cities as any)[provinceWatch] : undefined;

  const handleFinishForm = async (data: FormInputs) => {
    const contractId = contract?.contractId;
    if (!contractId) return;
    await completeProfile({
      address: data.address,
      birthDate: data.birthDate.format(dateFormats.ui.jalaliDate),
      cityEnglish: data.city,
      contractId,
      email: data.email,
      firstNameEnglish: data.firstName,
      lastNameEnglish: data.lastName,
      mobileNumber: data?.phoneNumber ?? "",
      nationalCode: data.nationalId.toString(),
      nationalIdSeries: data.idCardSerialNumber,
      postalCode: data.postalCode.toString(),
      provinceEnglish: data.province,
      isMale: data.sex === "male",
    });
  };

  useEffect(() => {
    if (profileInfoState.value) {
      form.setFieldValue("firstName", profileInfoState.value.firstNameEnglish ?? "");
      form.setFieldValue("lastName", profileInfoState.value.lastNameEnglish ?? "");
      form.setFieldValue("nationalId", profileInfoState.value?.nationalCode ?? "");
      form.setFieldValue("sex", profileInfoState.value?.isMale === false ? "female" : "male");
      form.setFieldValue("idCardSerialNumber", profileInfoState.value?.nationalIdSeries ?? "");
      form.setFieldValue("postalCode", profileInfoState.value?.postalCode ?? "");
      form.setFieldValue("email", profileInfoState.value?.email ?? "");
      form.setFieldValue("city", profileInfoState.value.cityEnglish ?? undefined);
      form.setFieldValue("province", profileInfoState.value.provinceEnglish ?? undefined);
      form.setFieldValue("address", profileInfoState.value.address ?? undefined);
      form.setFieldValue("phoneNumber", profileInfoState.value.mobileNumber ?? undefined);
      form.setFieldValue(
        "birthDate",
        profileInfoState.value.birthDate
          ? moment(profileInfoState.value.birthDate, dateFormats.ui.jalaliDate)
          : undefined,
      );
    }
  }, [form, profileInfoState]);

  useEffect(() => {
    const currentCity = form.getFieldValue("city");
    if (!provinceCitiesList) {
      return;
    }
    if (!provinceCitiesList.children.find((i: any) => i.CityNameEn === currentCity)) {
      form.setFieldValue("city", undefined);
    }
  }, [form, provinceCitiesList, provinceWatch]);

  if (profileInfoState.loading || (profileInfoState as any).error === "AuthError") {
    return (
      <Flex justify="center" align="center" className="tw-h-full tw-min-h-44 tw-w-full">
        <Spin />
      </Flex>
    );
  }

  return (
    <Flex className="tw-w-full tw-p-8">
      <Flex vertical className="tw-w-full tw-max-w-[488px]" gap={"32px"}>
        <Typography.Title level={5} className="!tw-m-0">
          {t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.title")}
        </Typography.Title>
        <Card.Ant size="small">
          <Flex vertical className="tw-w-full" gap={"small"}>
            <Flex align="center" justify="space-between" className="tw-w-full">
              <Typography.Text type="secondary">
                <strong>{t("common.fistName")}</strong>
              </Typography.Text>
              <Typography.Text>{personalInfo?.firstName ?? ""}</Typography.Text>
            </Flex>
            <Flex align="center" justify="space-between" className="tw-w-full">
              <Typography.Text type="secondary">
                <strong>{t("common.lastName")}</strong>
              </Typography.Text>
              <Typography.Text>{personalInfo?.lastName ?? ""}</Typography.Text>
            </Flex>
            <Flex align="center" justify="space-between" className="tw-w-full">
              <Typography.Text type="secondary">
                <strong>{t("common.birthDate")}</strong>
              </Typography.Text>
              <Typography.Text>
                {personalInfo?.birthDate || profileInfoState?.value?.birthDate
                  ? toJalali({
                      date: personalInfo?.birthDate ?? profileInfoState?.value?.birthDate,
                      dateFormat: dateFormats.ui.utcDateTime,
                      toFormat: dateFormats.ui.utcDate,
                    }).string
                  : ""}
              </Typography.Text>
            </Flex>
            <Flex align="center" justify="space-between" className="tw-w-full">
              <Typography.Text type="secondary">
                <strong>{t("common.nationalCode")}</strong>
              </Typography.Text>
              <Typography.Text>
                {personalInfo?.nationalCode ?? profileInfoState.value?.nationalCode ?? ""}
              </Typography.Text>
            </Flex>
          </Flex>
        </Card.Ant>
        <Form<FormInputs> form={form} layout="vertical" className="tw-w-full" onFinish={handleFinishForm}>
          <Row gutter={[16, 16]}>
            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.firstName.label")}
                name={"firstName"}
                className="!tw-m-0"
                rules={[
                  { required: true },
                  {
                    validator(_, value) {
                      if (!/^[a-zA-Z ]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            t("common.validations.onlyEnglishLetters", {
                              name: t(
                                "shop.bnpl.activation.formDrawer.steps.informationCompleting.form.firstName.label",
                              ),
                            }),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.lastName.label")}
                name={"lastName"}
                className="!tw-m-0"
                rules={[
                  { required: true },
                  {
                    validator(_, value) {
                      if (!/^[a-zA-Z ]*$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            t("common.validations.onlyEnglishLetters", {
                              name: t(
                                "shop.bnpl.activation.formDrawer.steps.informationCompleting.form.lastName.label",
                              ),
                            }),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.birthDate.label")}
                name={"birthDate"}
                className="!tw-m-0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker className="tw-w-full" />
              </Form.Item>
            </Col>
            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.sex.label")}
                name={"sex"}
                className="!tw-m-0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  options={[
                    {
                      value: "male",
                      label: t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.sex.options.male"),
                    },
                    {
                      value: "female",
                      label: t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.sex.options.female"),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.nationalId.label")}
                name={"nationalId"}
                className="!tw-m-0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                label={
                  <>
                    {t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.idCardSerialNumber.label")}
                    <Typography.Text type="secondary">
                      {t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.trackingCode")}
                    </Typography.Text>
                  </>
                }
                name={"idCardSerialNumber"}
                className="!tw-m-0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                rules={[{ required: true }]}
                name={"province"}
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.province.label")}
                tooltip={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.province.chooseAnOption")}
              >
                <Select>
                  {provinces.map((item) => (
                    <Select.Option key={item.ProvinceAbbreviation} value={item.ProvinceAbbreviation}>
                      {item.ProvinceNameFa}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={xs ? 24 : 12}>
              <Form.Item<FormInputs>
                rules={[{ required: true }]}
                name={"city"}
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.city.label")}
                tooltip={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.city.chooseAnOption")}
              >
                <Select disabled={!provinceWatch}>
                  {provinceCitiesList
                    ? provinceCitiesList?.children.map((item: any) => (
                        <Select.Option key={item.CityNameEn} value={item.CityNameEn}>
                          {item.CityNameFa}
                        </Select.Option>
                      ))
                    : null}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item<FormInputs>
                rules={[{ required: true }, { max: 180, message: t("formValidate.max", { max: 180 }) }]}
                name={"address"}
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.address.label")}
              >
                <TextArea
                  maxLength={180}
                  placeholder={t(
                    "shop.bnpl.activation.formDrawer.steps.informationCompleting.form.address.placeholder",
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.phoneNumber.label")}
                name={"phoneNumber"}
                className="!tw-m-0"
                rules={[
                  { required: true },
                  {
                    validator(_, phoneNumber) {
                      if (!phoneNumber) {
                        return Promise.resolve();
                      }
                      if (ValidLocalPhoneNumber(phoneNumber)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t("common.validations.localPhoneNumber", {
                            name: t(
                              "shop.bnpl.activation.formDrawer.steps.informationCompleting.form.phoneNumber.label",
                            ),
                          }),
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input className="tw-w-full" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.postalCode.label")}
                name={"postalCode"}
                className="!tw-m-0"
                rules={[
                  { required: true },
                  {
                    validator(_, postalCode) {
                      if (!postalCode) {
                        return Promise.resolve();
                      }
                      if (ValidPostalCode(postalCode)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t("common.validations.postalCode", {
                            name: t(
                              "shop.bnpl.activation.formDrawer.steps.informationCompleting.form.postalCode.label",
                            ),
                          }),
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input.Number className="tw-w-full" inputMode="numeric" changeOnWheel={false} controls={false} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<FormInputs>
                label={t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.email.label")}
                name={"email"}
                className="!tw-m-0"
                rules={[
                  { required: true },
                  {
                    validator(_, emailValue) {
                      if (!emailValue) {
                        return Promise.resolve();
                      }
                      if (ValidEmail(emailValue)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t("common.validations.email", {
                            name: t("shop.bnpl.activation.formDrawer.steps.informationCompleting.form.email.label"),
                          }),
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Flex gap={"middle"}>
                <Button type="default" onClick={goBack} disabled={completeProfileState.loading}>
                  {t("shop.bnpl.activation.formDrawer.previousStep")}
                </Button>
                <Button onClick={() => form.submit()} type="primary" loading={completeProfileState.loading}>
                  {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </Flex>
    </Flex>
  );
};

export default BnplActivationPageFormInformationCompleting;
